import React from 'react';
import { Box } from '@chakra-ui/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { InvestmentSection } from '../components/beginnerTrackComponents';
import { CodeverseMentorSection } from '../components/CodeverseMentorSection';
import { useAppData } from '../store/dataStore';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAffiliateCode } from '../hooks/useAffiliateCode';
import { Helmet } from 'react-helmet-async';

// Import AI Mastery components
import AIMasteryHero from '../components/aimastery/AIMasteryHero';
import AIMasteryProblem from '../components/aimastery/AIMasteryProblem';
import AIMasteryWhoFor from '../components/aimastery/AIMasteryWhoFor';
import AIMasteryOutcomes from '../components/aimastery/AIMasteryOutcomes';
import AIMasteryMastery from '../components/aimastery/AIMasteryMastery';
import AIMasteryCurriculum from '../components/aimastery/AIMasteryCurriculum';
import AIMasteryTestimonials from '../components/aimastery/AIMasteryTestimonials';
import AIMasteryGuarantee from '../components/aimastery/AIMasteryGuarantee';
import AIMasteryFAQ from '../components/aimastery/AIMasteryFAQ';
import AIMasteryTrustBadges from '../components/aimastery/AIMasteryTrustBadges';
import AIMasteryCTA from '../components/aimastery/AIMasteryCTA';

// Icons
import { FaRocket, FaBrain, FaCode, FaChartLine, FaShieldAlt } from 'react-icons/fa';

const AIMasteryLanding = () => {
  const { appData } = useAppData();
  const { course3, studentsCount } = appData;
  const { trackEvent } = useAnalytics();

  // Initialize affiliate code tracking
  useAffiliateCode('ai_mastery_visit');

  // Centralized page data object
  const pageData = {
    hero: {
      badge: `${studentsCount}+ Developers Upskilled`,
      title: "Elite AI Engineering",
      subtitle: "Production-Grade Development & Career Acceleration",
      description: "Stop playing with ChatGPT. Start safely refactoring million-line codebases, leading AI-augmented teams, and commanding higher salaries. From AI dabbler to Your next level in 3 weeks.",
      ctaButton: {
        text: "Start 2026 Strong - Enroll at ₹9,999",
        href: "/enroll?courseId=3"
      },
      secondaryButton: {
        text: "Watch Free Preview",
        href: "/webinar"
      },
      visualPlaceholder: "HERO_ANIMATION: Side-by-side comparison - 'AI Dabbler vs AI Master' showing code review time, architecture quality, career trajectory"
    },

    problem: {
      title: "You're Using a Ferrari in First Gear",
      title_image: "/images/problem_title_image.jpeg",
      subtitle: "Yes, you use ChatGPT. But are you getting 10x results, not just 3x?",
      currentReality: {
        title: "What Most Experienced Developers Do",
        points: [
          "Using ChatGPT/Claude like a fancy Stack Overflow",
          "Copy-pasting code without systematic approach",
          "2-3x faster, not 10x faster",
          "Still spending 30-60 min debugging each AI-generated code block",
          "No consistent quality framework",
          "Can't train team effectively - everyone struggles individually"
        ]
      },
      actualJob: {
        title: "What Your Actual Job Requires",
        points: [
          "Safely modify 5-year-old production codebases (200k+ lines)",
          "Review 5 AI PRs in 25 minutes with confidence",
          "Generate architecture that passes principal engineer review",
          "Lead AI-augmented teams, not just code faster individually",
          "Refactor legacy systems without breaking production"
        ]
      },
      tagline: "The difference between dabbling and mastery? The difference could be ₹15 Lakh+ in CTC",
      visualPlaceholder: "COMPARISON_DIAGRAM: 'AI Dabbler' vs 'AI Master' - showing time spent, output quality, career level, compensation"
    },

    whoFor: {
      badge: "For Experienced Developers",
      title: "Is This For You?",
      subtitle: "This is NOT a beginner course. It's for developers who are already productive but ready to level up.",
      personas: [
        {
          icon: FaCode,
          title: "You Work on Production Systems",
          desc: "80% of your work is modifying existing code, not greenfield projects",
          check: true
        },
        {
          icon: FaBrain,
          title: "You Already Use AI, But...",
          desc: "Still spending 30-60min reviewing AI code, unsure how to train your team",
          check: true
        },
        {
          icon: FaRocket,
          title: "You Want Career Acceleration",
          desc: "Ready for Next level but need systematic AI mastery to stand out",
          check: true
        },
        {
          icon: FaChartLine,
          title: "You're Done with Trial & Error",
          desc: "No more random YouTube tutorials - want battle-tested production frameworks",
          check: true
        }
      ],
      realityCheck: {
        title: "Reality Check",
        questions: [
          "How long does it take you to confidently approve AI-generated code?",
          "Can you explain your AI workflow in the interview?",
          "Could you train your entire team on AI development systematically?",
          "When was the last time you built a greenfield project at work?",
        ]
      }
    },

    outcomes: {
      title: "What You'll Actually Achieve",
      subtitle: "Real outcomes for experienced developers, not toy project metrics",
      technicalMilestones: [
        {
          timeline: "After Session 2",
          achievement: "Safely add features to legacy 200k+ line codebases",
          impact: "What took 2 weeks now takes 2 days"
        },
        {
          timeline: "After Week 1",
          achievement: "Review 5 AI PRs in 25 minutes with systematic framework",
          impact: "Save 10+ hours/week on code reviews"
        },
        {
          timeline: "After Week 2",
          achievement: "Generate architecture that passes principal engineer review",
          impact: "Design 3 approaches in 5 minutes, evaluate like a senior"
        },
        {
          timeline: "After Week 3",
          achievement: "Train your entire team on AI-assisted development",
          impact: "10x team output, positioned for tech lead role"
        }
      ],
      careerMilestones: [
        {
          timeline: "Week 1",
          achievement: "Portfolio case studies that demonstrate senior-level work"
        },
        {
          timeline: "Month 1",
          achievement: "Lead AI transformation initiatives at your company"
        },
        {
          timeline: "Month 3",
          achievement: "Interview-ready for the positions which demands AI Mastery"
        },
        {
          timeline: "Month 6",
          achievement: "Promoted or landed senior role (typical: ₹15L salary jump)"
        }
      ],
      roiMetrics: {
        title: "ROI Calculator",
        timeSaved: "28.5 hours saved per week",
        breakdown: [
          { task: "Code reviews", before: "10 hrs", after: "2 hrs", saved: "8 hrs" },
          { task: "Architecture docs", before: "4 hrs", after: "0.5 hrs", saved: "3.5 hrs" },
          { task: "Bug fixes", before: "8 hrs", after: "3 hrs", saved: "5 hrs" },
          { task: "Feature development", before: "20 hrs", after: "8 hrs", saved: "12 hrs" }
        ],
        monthlyValue: "At ₹2,000/hour: ₹2.28 lakh monthly value",
        paybackPeriod: "Course pays for itself in 2 days",
        careerImpact: "First year salary impact: ₹15 lakh+"
      },
      visualPlaceholder: "" //ROI_CALCULATOR: Interactive diagram showing time savings per task + career trajectory graph
    },

    mastery: {
      title: "Production-Grade Skills That Get You Promoted",
      subtitle: "Not theoretical concepts - battle-tested frameworks from real production work",
      coreSkills: [
        {
          icon: FaShieldAlt,
          title: "Legacy Code Mastery (Session 2)",
          desc: "The skill that separates mid-level from senior: safely modify production systems with zero fear",
          techniques: [
            "Understand any codebase in 2 minutes",
            "Safe modification with AI guardrails",
            "Branch protection, test-first approach",
            "Technical debt quantification"
          ],
          impact: "80% of your actual job - finally have a system for it",
          visualPlaceholder: "" //DIAGRAM: Before/After legacy code workflow
        },
        {
          icon: FaBrain,
          title: "5-Minute Code Review Framework",
          desc: "Most devs waste 30-60 min per review. You'll do Accept/Iterate/Reject in 5 minutes",
          techniques: [
            "Systematic quality checklist",
            "Automated security scanning",
            "Performance anti-pattern detection",
            "Production-readiness scoring"
          ],
          impact: "Save 10+ hours/week = ₹50k+/month value",
          visualPlaceholder: "/images/codereview.jpeg"
        },
        {
          icon: FaCode,
          title: "AI as Senior Architect",
          desc: "Stop generating code - start architecting systems like principal engineers do",
          techniques: [
            "Generate 3 architecture approaches in 5 minutes",
            "Automated architecture reviews",
            "Dependency graphs & ADRs",
            "Trade-off analysis frameworks"
          ],
          impact: "This alone is worth the course price",
          visualPlaceholder: "" //SCREENSHOT: AI-generated architecture decision record
        },
        {
          icon: FaRocket,
          title: "Team Leadership & Training",
          desc: "Individual contributor → Team multiplier: Train entire team on AI development",
          techniques: [
            "90-minute team workshop materials",
            "Train-the-trainer certification",
            "Team productivity frameworks",
            "Charge ₹2k/hour for corporate training"
          ],
          impact: "Positioned for tech lead role immediately",
          visualPlaceholder: "" //PHOTO: Workshop in action placeholder
        },
        {
          icon: FaChartLine,
          title: "Career Acceleration System",
          desc: "Technical skills + positioning strategy = senior engineer compensation",
          techniques: [
            "Portfolio case study generator",
            "Interview prep: 'Explain your AI workflow'",
            "Salary negotiation templates",
            "LinkedIn/GitHub optimization"
          ],
          impact: "₹15L+ salary jump in first year (typical result)",
          visualPlaceholder: "" //GRAPH: Career trajectory - with vs without course"
        }
      ],
      promptPatterns: {
        title: "The 6 Prompt Patterns (Just the Beginning)",
        subtitle: "These are the instruction manual. The real value is the battle-tested prompts, tools, and frameworks.",
        patterns: [
          "EECC (Explain-Example-Constrain-Check)",
          "SIRCOP (Safe Incremental Refactor with Context Optimization)",
          "Test-First Development",
          "Surgical Edit (legacy code)",
          "Architecture Review",
          "Documentation Generation"
        ],
        fullLibrary: "Access readymade production-ready prompts for every scenario"
      }
    },

    curriculum: {
      title: "Production-Focused 3",
      description: "11 core sessions + 2 bonus sessions - Designed for your actual job, not toy projects",
      subtitle: "Most AI courses teach 'ONLY' building from scratch. We teach safely modifying production systems.",
      weekByWeek: [
        {
          week: "Week 1: Foundation + Real-World Skills",
          sessions: [
            {
              number: 1,
              title: "The AI Developer Awakening",
              description: "See the before/after live: 30 min traditional vs 3 min with AI",
              keyTakeaway: "Understand why you're stuck at 3x instead of 10x"
            },
            {
              number: 2,
              title: "Legacy Code Mastery",
              description: "The skill 80% of courses ignore - safely modify production systems",
              keyTakeaway: "Add features to 200k+ line codebase with zero fear",
              badge: "MOST VALUABLE SESSION",
              visualPlaceholder: "BADGE_ICON: 'Hit your pain point immediately'"
            },
            {
              number: 3,
              title: "Lightning-Fast Implementation",
              description: "Ship features in hours that used to take weeks",
              keyTakeaway: "Production deployment pipeline with AI assistance"
            }
          ]
        },
        {
          week: "Week 2: Architecture + Quality Systems",
          sessions: [
            {
              number: 4,
              title: "AI as Senior Architect",
              description: "Generate architecture that passes principal engineer review",
              keyTakeaway: "Automated ADRs, dependency analysis, trade-off evaluation", 
              image: ""

            },
            {
              number: 5,
              title: "5-Minute Code Review Framework",
              description: "Accept/Iterate/Reject in 5 minutes instead of 60 minutes",
              keyTakeaway: "Save 10+ hours/week on code reviews",
              image: "/images/codereview.jpeg"
            },
            {
              number: 6,
              title: "Prompt Engineering Mastery",
              description: "The 6 core patterns + battle-tested prompt library",
              keyTakeaway: "Systematic approach for every development scenario",
              image: ""
            },
            {
              number: 7,
              title: "Testing at AI Speed",
              description: "90%+ test coverage in 20 minutes",
              keyTakeaway: "Quality assurance without the time sink",
              image: ""
            }
          ]
        },
        {
          week: "Week 3: Leadership + Career Acceleration",
          sessions: [
            {
              number: 8,
              title: "DevOps Superpowers",
              description: "Complete CI/CD pipeline in 30 minutes",
              keyTakeaway: "Infrastructure as code with AI assistance"
            },
            {
              number: 9,
              title: "Team Leadership & Training",
              description: "Train your team - become internal AI coach",
              keyTakeaway: "Certification + materials to charge ₹2k/hour",
              badge: "CAREER MULTIPLIER"
            },
            {
              number: 10,
              title: "Production Incident Response",
              description: "Use AI during P0 incidents, root cause analysis",
              keyTakeaway: "Be the hero who fixes critical bugs in minutes"
            },
            {
              number: 11,
              title: "Career Acceleration Masterclass",
              description: "Portfolio, resume, interview prep, negotiation",
              keyTakeaway: "Position yourself for ₹15L+ salary jump"
            }
          ]
        }
      ],
      bonusSessions: [
        {
          title: "AI for Code Migrations",
          description: "React 16→18, Python 3.8→3.11, dependency hell solved (Already given as part of curriculum)"
        },
        {
          title: "Build a Project in 1 Session - Marathon",
          description: "4-6 hour intensive session: Take a production app from concept to deployment in one sitting with AI assistance"
        }
      ],
      visualPlaceholder: "" //TIMELINE_GRAPHIC: 3-week journey with key milestones marked
    },

    testimonials: {
      title: "Real Results from Experienced Developers",
      subtitle: "Not generic praise - specific outcomes that matter for your career",
      stories: [
        {
          quote: "Session 2 alone paid for the course. I can now confidently refactor our 300k-line legacy codebase. Went from avoiding legacy work to becoming the go-to person for it.",
          author: "Senior Engineer, 16 years experience",
          outcome: "Promoted to Lead Engineer within 4 months",
          metric: "Salary jump: ₹22L → ₹38L",
          visualPlaceholder: "" //PHOTO: Professional headshot placeholder
        },
        {
          quote: "The 5-minute code review framework saved me 12+ hours every week. Now I'm leading AI adoption for our 15-person team. Got promoted because I could train everyone else.",
          author: "Full-stack Developer, 5 years",
          outcome: "Internal AI coach, training entire engineering org",
          metric: "Now bills ₹3k/hour for corporate training",
          visualPlaceholder: "" //"PHOTO: Professional headshot placeholder
        },
        {
          quote: "I was using ChatGPT daily but still slow. The systematic approach made me 10x faster - not 3x. Within 2 months, I was interviewing for senior positions with confidence.",
          author: "Backend Developer, 4 years experience",
          outcome: "Landed senior role at startup",
          metric: "Salary jump: ₹18L → ₹32L + equity",
          visualPlaceholder: "" //PHOTO: Professional headshot placeholder
        },
        {
          quote: "Most valuable: Learning to safely AI-modify production code. I used to fear touching legacy systems. Now I do it daily. My manager asks how I ship features so fast.",
          author: "Software Engineer, 3 years experience",
          outcome: "Became team's productivity benchmark",
          metric: "Ships 4x more features per sprint",
          visualPlaceholder: "" //PHOTO: Professional headshot placeholder
        },
        {
          quote: "The career acceleration module was unexpected gold. Portfolio optimization + interview prep landed me 3 senior role offers. Negotiated ₹15L higher because I could demonstrate AI mastery.",
          author: "Developer, 6 years experience",
          outcome: "3 competing senior engineer offers",
          metric: "Negotiated ₹45L (started at ₹30L ask)",
          visualPlaceholder: "" //PHOTO: Professional headshot placeholder
        }
      ],
      aggregateMetrics: {
        title: "Aggregate Participant Outcomes",
        stats: [
          { metric: "70%", description: "Report 5-10x productivity gains within first month" },
          { metric: "45%", description: "Promoted or changed jobs within 6 months" },
          { metric: "₹15L", description: "Average salary increase for promoted participants" },
          { metric: "28.5hrs", description: "Average time saved per week" }
        ],
        visualPlaceholder: "" //STATS_GRID: Animated counters with icons
      }
    },

    investment: {
      leftSide: {
        title: "What You're Actually Getting",
        subtitle: "Not ₹5k for 6 prompts - ₹5k for ₹1.5L worth of career acceleration",
        description: "Stop thinking course price. Think investment ROI: ₹5k invested, ₹15L+ career impact in year 1.",
        valueBreakdown: [
          {
            item: "13 Live Sessions + Recordings",
            value: "₹30,000",
            description: "Production-focused curriculum designed for experienced developers"
          },
          {
            item: "Codeverse Mentor VS Code Extension",
            value: "₹24,000",
            description: "₹2,000/month × 12 months - Guided projects, real-time validation"
          },
          {
            item: "Production-Ready Prompt Library",
            value: "₹10,000",
            description: "battle-tested prompts refined from 1000s of hours"
          },
          {
            item: "Code Review Automation Toolkit",
            value: "₹25,000",
            description: "GitHub Actions, pre-merge checks, security scanning"
          },
          {
            item: "ADR Generator & Architecture Tools",
            value: "₹15,000",
            description: "Auto-generate architecture decision records, dependency analysis"
          },
          {
            item: "Career Acceleration Package",
            value: "₹30,000",
            description: "Portfolio optimization, resume, interview prep, negotiation templates"
          },
          {
            item: "Train-the-Trainer Certification",
            value: "₹50,000",
            description: "Materials to train teams at ₹2k-3k/hour corporate rate"
          },
          {
            item: "Legacy Code Analysis Templates",
            value: "₹20,000",
            description: "One-click codebase analysis, tech debt quantification"
          },
          {
            item: "Lifetime Alumni Network",
            value: "Priceless",
            description: "Weekly office hours, job referrals, real problem solving"
          }
        ],
        totalValue: "₹2,04,000",
        comparison: "Compare: Traditional bootcamps ₹2L+ with zero career support | 6-12 months of trial & error"
      },
      pricing: {
        badge: 'New Year Launch - 97% OFF Total Value',
        mrp: course3.mrp,
        price: course3.price,
        discountText: 'Early bird pricing ends January 9th, 2026',
        emi: course3.emi,
        guarantee: '2-Session Money-Back Guarantee',
        ctaHref: '/enroll?courseId=3',
        paybackCalculator: {
          title: "When This Pays For Itself",
          scenarios: [
            { timeline: "2 days", description: "Time saved at ₹2k/hour billing rate" },
            { timeline: "1 month", description: "First corporate training gig" },
            { timeline: "3 months", description: "When you start leading team AI initiatives" },
            { timeline: "6 months", description: "Typical promotion timeline (₹15L salary jump)" }
          ]
        },
        included: [
          '13 live sessions (11 core + 2 bonus)',
          '2 production-grade projects (deployed)',
          'Comprehensive list of prompt pattern library',
          'Code review automation toolkit',
          'ADR & architecture tool suite',
          'Career acceleration package',
          'Train-the-trainer certification',
          'Codeverse Mentor VS Code extension',
          '1:1 mentoring sessions',
          'Portfolio review & optimization',
          'Private Slack community',
          'Weekly office hours (lifetime)',
          'Lifetime curriculum updates'
        ]
      },
      visualPlaceholder: "" //VALUE_STACK_ANIMATION: Items stacking up to show ₹2.04L total value vs ₹9,999 price
    },

    guarantee: {
      icon: FaShieldAlt,
      title: "2-Session Money-Back Guarantee",
      description: "If you don't see real value after 2 sessions, get a full refund. No questions asked. That's how confident we are in this system.",
      features: [
        "100% refund if not satisfied",
        "Keep all materials accessed",
        "Zero risk to you",
        "Learn by doing, risk-free",

      ]
    },

    faq: {
      title: "Addressing Your Objections Directly",
      description: "For experienced developers who are skeptical (as you should be)",
      items: [
        {
          q: "I already use AI tools daily - why do I need this course?",
          a: "Fair question. Let me be direct: When AI generates code for you, how long does it take to review and decide if it's production-ready? If you're spending 20-60 minutes per review, you're dabbling, not mastering. This course teaches you to do that in 5 minutes with a systematic framework, AND train your entire team to do the same. That's 100+ hours saved per month for your team. Still think you've mastered AI-assisted development?",
          realDifferentiators: [
            "The 5-Minute Code Review Framework (saves 10+ hrs/week = ₹50k/month)",
            "AI as Senior Architect (automated architecture reviews, not just code generation)",
            "Legacy Code Mastery (80% of your actual job - finally have a system)",
            "Team Multiplication (train others, become tech lead material immediately)"
          ]
        },
        {
          q: "I work on existing projects, not greenfield apps. Is this relevant?",
          a: "Perfect! That's exactly why I built this. 80% of the course is about existing codebases. Session 2 alone—Legacy Code Mastery—teaches you to safely add features to production systems with millions of users. Most AI courses teach building Todo apps from scratch. We teach the actual job: safely adding features to a 5-year-old React codebase with 200k lines, zero documentation, original devs gone, serving 1M users in production. When was the last time you built a greenfield project at work? Exactly.",
          specificSkills: [
            "Understand any codebase in 2 minutes",
            "Safe AI-assisted refactoring with guardrails",
            "Technical debt quantification and prioritization",
            "Dependency migration (React 16→18, Python upgrades)",
            "Production incident response with AI"
          ]
        },
        {
          q: "Why pay ₹5k for 6 prompts I could find on Twitter?",
          a: "If this was just 6 prompts. You're getting: (1) ₹1.5L worth of tools - ADR generator, code review automation, prompt library, VS Code extension; (2) Certification to train others (₹50k earning potential); (3) Career acceleration package (₹15L salary impact in year 1); (4) 28+ hours saved every week (₹2.28L/month at ₹2k/hour billing rate). The course pays for itself in 2 days. The 6 prompts? Just the instruction manual.",
          valueBreakdown: "See full breakdown in pricing section - ₹2.04L total value"
        },
        {
          q: "Can't I just learn this from YouTube/ChatGPT over time?",
          a: "You can - in 6-12 months of trial and error, making costly mistakes on production code. Or master it systematically in 3 weeks with battle-tested frameworks from actual production work. Consider: How much is 6 months of your time worth? How much could you earn if you reached senior level 6 months faster? The math is obvious."
        },
        {
          q: "I'm a senior developer already. Is this too basic?",
          a: "This is NOT a basic course. It's for senior developers who want to reach principal/lead level. If you can already: (1) Review AI PRs in 5 minutes confidently, (2) Generate architecture that passes principal review, (3) Train your entire team systematically, (4) Safely refactor legacy systems with AI, then you don't need this. But if any of those made you pause, you'll benefit massively."
        },
        {
          q: "How much time commitment? I have a full-time job.",
          a: "90 min per live session (3 sessions/week) + 10 hours practice per week = ~14 hours/week for 3 weeks. If you can't commit 14 hours/week to potentially jumping from ₹20L to ₹35L compensation, this isn't the right time. But if you can, the ROI is massive. All sessions recorded if you miss live."
        },
        {
          q: "Will this work with my tech stack? (Python/Java/Go/etc.)",
          a: "Principles apply to any language/framework. Live examples use React/FastAPI because they're popular, but participants have successfully applied patterns to Python/Django, Java/Spring, Go microservices, .NET, Vue, Angular, etc. The frameworks are language-agnostic - prompt patterns, code review systems, architecture approaches work everywhere."
        },
        {
          q: "What if I don't get results? Is there a refund?",
          a: "2-Session Money-Back Guarantee. If you don't see real value after attending first 2 sessions (including the Legacy Code Mastery session), get full refund. No questions asked. Keep all materials accessed. That's how confident we are. Less than 2% request refunds."
        },
        {
          q: "Why is this so cheap compared to bootcamps charging Huge money?",
          a: "No fancy office, no celebrity endorsements, no bloated overhead, no sales team. Just quality content at fair price from a practicing developer who uses these techniques daily. I'd rather have 100 successful students at ₹5k than 10 stressed students at ₹50k. The goal is impact, not profit maximization. Plus, massive New Year launch discount."
        },
        {
          q: "Will AI replace developers? Should I even invest in learning this?",
          a: "AI won't replace developers. It will replace developers who don't learn to use AI systematically. In 2026, there will be two types of developers: (1) Those scrambling to catch up with AI-augmented peers, (2) Those leading AI transformation and commanding premium compensation. This course puts you firmly in category 2. The developers being replaced are those who refuse to adapt."
        }
      ]
    },

    trustBadges: {
      title: "Why Trust This Course?",
      subtitle: "Because you're skeptical (as experienced developers should be)",
      badges: [
        {
          title: "Not Selling Hype",
          items: [
            "Every technique demonstrated live in Session 1",
            "2-session money-back guarantee (less than 2% request refunds)",
            "Complete curriculum visible upfront - no hidden upsells",
            "Real production codebases, not Todo apps",
            "Actual participant outcomes with metrics (see testimonials)"
          ]
        },
        {
          title: "Built by Practitioners, Not Marketers",
          items: [
            "Methods used daily on production systems serving millions",
            "Tested through actual client projects (₹50L+ worth)",
            "No theoretical BS - only what works in real codebases",
            "Updated monthly as AI tools evolve",
            "Instructor actively ships code, not just teaches"
          ]
        },
        {
          title: "For Experienced Developers",
          items: [
            "Not dumbed down for beginners",
            "Assumes you already code professionally",
            "Focuses on production systems (80% legacy code work)",
            "Senior-level concepts: architecture, review frameworks, team leadership",
            "Career acceleration, not just technical skills"
          ]
        },
        {
          title: "Proven ROI",
          items: [
            "70% report 5-10x productivity gains within first month",
            "45% promoted or changed jobs within 6 months",
            "₹15L average salary increase for promoted participants",
            "28.5 hours average time saved per week",
            "Course pays for itself in 2 days (at ₹2k/hour rate)"
          ]
        }
      ],
      finalStatement: {
        title: "The Brutal Truth",
        text: "If you're looking for a magic bullet that requires zero effort, this isn't it. If you want a systematic approach to dominate 2026 as an AI-augmented senior engineer, and you're willing to invest 3 weeks of focused effort, this is exactly what you need."
      },
      visualPlaceholder: "" //TRUST_INDICATORS: Badges with checkmarks, participant count, avg rating, money-back guarantee seal
    },

    cta: {
      badge: `Next Cohort Starts: ${course3.startDate}`,
      title: "2025 Was the Year AI Tools Exploded. 2026 Is the Year You Master Them.",
      subtitle: "Two Types of Developers in 2026",
      developerTypes: {
        type1: {
          label: "The Scrambler",
          traits: [
            "Still using ChatGPT like Stack Overflow",
            "3x faster but plateaued",
            "Can't explain their AI workflow in interviews",
            "Watching AI-augmented peers get promoted",
            "Stuck at same compensation level"
          ]
        },
        type2: {
          label: "The Master",
          traits: [
            "10x faster with systematic approach",
            "Leading AI transformation at their company",
            "Training entire teams on AI development",
            "Commands higher compensation",
            "Getting recruited for their AI mastery"
          ]
        }
      },
      question: "Which developer will you be?",
      urgency: {
        title: "Why Start Now",
        reasons: [
          "Every week you wait, AI-augmented peers pull further ahead",
          "Senior positions are being filled by developers with proven AI mastery",
          "New Year launch pricing ends January 4th (never this low again)",
          "Next cohort limited to 30 developers (maintain quality)",
          "The longer you delay, the more your peers compound their advantage"
        ]
      },
      primaryButton: {
        text: "Secure Your Spot at ₹9,999",
        href: "/enroll?courseId=3"
      },
      secondaryButton: {
        text: "Watch Free Preview Session",
        href: "/webinar"
      },
      tertiaryButton: {
        text: "Still Have Questions? Talk to Us",
        href: "/contact"
      },
      footer: "Only 30 seats • 2-session money-back guarantee • ROI proven • Career impact guaranteed",
      finalThought: "Your peers are already learning these systems. The question isn't if you should master AI-augmented development. It's whether you'll lead the change or scramble to catch up.",
      visualPlaceholder: "" //SPLIT_SCREEN_ANIMATION: 'Scrambler' career path vs 'Master' career path over 12 months
    },

    codeverseMentor: {
      badge: 'Exclusive Learning Tool',
      title: 'Accelerate with Codeverse Mentor',
      subtitle: 'ChatGPT gives answers. Codeverse Mentor builds production-ready developers.',
      comparisonTable: {
        codeverseColumn: 'Codeverse Mentor',
        otherToolsColumn: 'ChatGPT & Other AI',
        rows: [
          { feature: 'Proactive step-by-step guidance' },
          { feature: 'Real-time code validation' },
          { feature: 'Context-aware assistance' },
          { feature: 'Zero prompt anxiety' },
          { feature: 'Progress tracking with milestones' },
          { feature: 'Builds independence through micro-steps' },
        ],
      },
      featuresTitle: 'How Codeverse Mentor Accelerates Your Learning',
      features: [
        {
          title: 'Production-Ready Project Journey',
          description: 'Advanced projects broken into achievable milestones with clear objectives and validation criteria. Build complex systems step-by-step with confidence.',
        },
        {
          title: 'Real-Time Code Validation',
          description: 'Instant feedback on your code directly in VS Code. Catch issues early, follow best practices, and ensure production-quality code from the start.',
        },
        {
          title: 'Context-Aware AI Assistance',
          description: 'Get help tailored to your specific implementation. Pre-structured prompts for advanced scenarios eliminate guesswork and accelerate development.',
        },
        {
          title: 'Advanced Learning Paths',
          description: 'Master production-grade patterns and architectures used by leading AI companies. Build skills that translate directly to real-world projects.',
        },
        {
          title: 'Smart Debugging Support',
          description: 'Get unstuck on complex issues without losing learning value. Develop problem-solving skills essential for professional development.',
        },
        {
          title: 'Project Milestone Tracking',
          description: 'Track your progress through production-grade projects. Visualize achievement milestones and stay motivated as you build portfolio-worthy applications.',
        },
      ],
      freeAccess: {
        badge: 'Included Free',
        title: 'Free Access for Entire Course Duration',
        description: 'Build production-grade AI applications faster with our proprietary VS Code extension. Available exclusively to all enrolled students at no additional cost.',
        buttonText: 'Learn More About Codeverse Mentor',
        link: 'https://marketplace.visualstudio.com/items?itemName=OjasaMirai.codeverse-mentor',
      },
    },
  };

  // Event handlers
  const handleEnrollClick = (section) => {
    trackEvent('pricing_cta_click', {
      page: 'ai_mastery',
      plan: 'ai_mastery_7_week',
      price: course3.price,
      discount: 'new_year_launch',
      section: section
    });
  };

  return (
    <>
      <Helmet>
        <title>Elite AI Engineering | Production-Grade Development & Career Acceleration</title>
        <meta
          name="description"
          content="From AI dabbler to senior engineer in 3 weeks. Master production-grade AI development, safely refactor legacy code, lead AI-augmented teams. For experienced developers ready to command senior compensation."
        />
        <meta name="keywords" content="AI engineering mastery, legacy code AI, senior developer course, AI career acceleration, production AI development, AI code review, systematic AI development, tech lead training" />
      </Helmet>

      <Box>
        <Navbar />
        <AIMasteryHero data={pageData.hero} />
        <AIMasteryProblem data={pageData.problem} />
        <AIMasteryWhoFor data={pageData.whoFor} />
        <AIMasteryOutcomes data={pageData.outcomes} />
        <AIMasteryMastery data={pageData.mastery} />
        <AIMasteryCurriculum data={pageData.curriculum} />
        <CodeverseMentorSection data={pageData.codeverseMentor} />
        {/*
        <AIMasteryTestimonials data={pageData.testimonials} />
        */}
        <InvestmentSection
          data={pageData.investment}
          onEnrollClick={() => handleEnrollClick('pricing')}
        />
        <AIMasteryGuarantee data={pageData.guarantee} />
        <AIMasteryTrustBadges data={pageData.trustBadges} />
        <AIMasteryFAQ data={pageData.faq} />
        <AIMasteryCTA
          data={pageData.cta}
          onEnrollClick={() => handleEnrollClick('final_cta')}
        />
        <Footer />
      </Box>
    </>
  );
};

export default AIMasteryLanding;
